import React, { Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import {
  ACCESS_ROLES_FOR_CAMPAIGNS,
  ACCESS_ROLES_FOR_GRAPHICS_STATISTICS,
  ACCESS_ROLES_FOR_MAP,
  ACCESS_ROLES_FOR_MODERATION,
  ACCESS_ROLES_FOR_REPORTS,
  ACCESS_ROLES_FOR_STATISTICS,
} from '@/api/routes/users/constants';
import { ROLES } from '@/api/routes/users/types';
import ElementWrapper from '@/components/ElementWrapper/ElementWrapper';
import ErrorProvider from '@/components/ErrorPage/ErrorProvider';
import RequireAuth from '@/components/RequireAuth/RequireAuth';
import Admin from '@/pages/admin/Admin';
import AdminIndex from '@/pages/admin/index';
import Login from '@/pages/root/login/Login';
import Root from '@/pages/root/Root';
import LoaderContainer from '../LoaderContainer/LoaderContainer';

const Dashboard = React.lazy(() => import('@/pages/admin/dashboard/Dashboard'));
const Moderation = React.lazy(() => import('@/pages/admin/moderation/Moderation'));
const MapPage = React.lazy(() => import('@/pages/admin/map/MapPage'));
const CampaignIndex = React.lazy(() => import('@/pages/admin/campaigns/index'));
const Campaign = React.lazy(() => import('@/pages/admin/campaigns/campaign/Campaign'));
const Campaigns = React.lazy(() => import('@/pages/admin/campaigns/Campaigns'));
const Reports = React.lazy(() => import('@/pages/admin/reports'));
const ReportTable = React.lazy(() => import('@/pages/admin/reports/reportTable'));
const Report = React.lazy(() => import('@/pages/admin/reports/report'));
const Table = React.lazy(() => import('@/pages/admin/table/Table'));
const Graphics = React.lazy(() => import('@/pages/admin/graphics/Graphics'));
const IncidentsIndex = React.lazy(() => import('@/pages/admin/incidents/index'));
const Incidents = React.lazy(() => import('@/pages/admin/incidents/Incidents'));
const Full = React.lazy(() => import('@/pages/admin/incidents/full/Full'));
const Cameras = React.lazy(() => import('@/pages/admin/cameras/Cameras'));
const Views = React.lazy(() => import('@/pages/admin/views/Views'));
const IgnitionIndex = React.lazy(() => import('@/pages/admin/ignition/index'));
const Ignition = React.lazy(() => import('@/pages/admin/ignition/Ignition'));
const Visualisation = React.lazy(() => import('@/pages/admin/visualisation/Visualisation'));
const UsersIndex = React.lazy(() => import('@/pages/admin/users/index'));
const Users = React.lazy(() => import('@/pages/admin/users/Users'));
const User = React.lazy(() => import('@/pages/admin/users/user/User'));
const ProfilesIndex = React.lazy(() => import('@/pages/admin/profiles/index'));
const Profiles = React.lazy(() => import('@/pages/admin/profiles/Profiles'));
const Profile = React.lazy(() => import('@/pages/admin/profiles/profile/Profile'));
const Settings = React.lazy(() => import('@/pages/admin/settings/Settings'));

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <ErrorProvider />,
    children: [
      {
        index: true,
        element: <Login />,
      },
    ],
  },
  {
    path: '/admin',
    element: (
      <RequireAuth>
        <Admin />
      </RequireAuth>
    ),
    errorElement: <ErrorProvider />,
    children: [
      {
        index: true,
        element: <AdminIndex />,
      },
      {
        path: 'moderation',
        element: (
          <ElementWrapper roles={ACCESS_ROLES_FOR_MODERATION}>
            <Moderation />
          </ElementWrapper>
        ),
      },
      {
        path: 'campaigns',
        element: (
          <ElementWrapper roles={ACCESS_ROLES_FOR_CAMPAIGNS}>
            <Campaigns />
          </ElementWrapper>
        ),
        children: [
          {
            index: true,
            element: <Suspense fallback={<LoaderContainer loading={true} />}>
              <CampaignIndex />
            </Suspense>,
          },
          {
            path: ':campaignId',
            element: (
              <ElementWrapper roles={ACCESS_ROLES_FOR_CAMPAIGNS}>
                <Campaign />
              </ElementWrapper>
            ),
          },
        ],
      },
      {
        path: 'reports',
        element: (
          <ElementWrapper roles={ACCESS_ROLES_FOR_REPORTS}>
            <Reports />
          </ElementWrapper>
        ),
        children: [
          {
            index: true,
            element: <Suspense fallback={<LoaderContainer loading={true} />}><ReportTable /></Suspense>,
          },
          {
            path: ':reportId',
            element: (
              <ElementWrapper roles={ACCESS_ROLES_FOR_REPORTS}>
                <Report />
              </ElementWrapper>
            ),
          },
        ],
      },
      {
        path: 'map',
        element: (
          <ElementWrapper
            roles={ACCESS_ROLES_FOR_MAP}
          >
            <MapPage />
          </ElementWrapper>
        ),
      },
      {
        path: 'table',
        element: <ElementWrapper roles={[ROLES.STAT_VIEW, ROLES.VIEW, ROLES.STAT_DEVICE_PROFILE_VIEW]}><Table /></ElementWrapper>,
      },
      {
        path: 'graphics',
        element: <ElementWrapper roles={ACCESS_ROLES_FOR_GRAPHICS_STATISTICS}><Graphics /></ElementWrapper>,
      },
      {
        path: 'incidents',
        element: <ElementWrapper roles={[ROLES.INCIDENT_VIEW, ROLES.VIEW]}><Incidents /></ElementWrapper>,
        children: [
          {
            index: true,
            element: <Suspense fallback=""><IncidentsIndex /></Suspense>,
          },
          {
            path: 'full',
            element: <Suspense fallback=""><Full /></Suspense>,
          },
        ],
      },
      {
        path: 'views',
        element: <ElementWrapper roles={ACCESS_ROLES_FOR_STATISTICS}><Views /></ElementWrapper>,
      },
      {
        path: 'users',
        element: <ElementWrapper roles={[ROLES.VIEW]}><Users /></ElementWrapper>,
        children: [
          {
            index: true,
            element: <Suspense fallback={<LoaderContainer loading={true} />}><UsersIndex /></Suspense>,
          },
          {
            path: ':userId',
            element: <Suspense fallback={<LoaderContainer loading={true} />}><User /></Suspense>,
          },
        ],
      },
      {
        path: 'profiles',
        element: (
          <ElementWrapper
            roles={[ROLES.VIEW]}
            additionalRequiredRoles={[ROLES.PROFILE_VIEW, ROLES.PROFILE_EDIT]}
          >
            <Profiles />
          </ElementWrapper>
        ),
        children: [
          {
            index: true,
            element: <Suspense fallback={<LoaderContainer loading={true} />}><ProfilesIndex /></Suspense>,
          },
          {
            path: ':profileId',
            element: <Suspense fallback={<LoaderContainer loading={true} />}><Profile /></Suspense>,
          },
        ],
      },
      {
        path: 'settings',
        element: <ElementWrapper roles={[ROLES.VIEW]}><Settings /></ElementWrapper>,
      },
    ],
  },
]);

export default router;
